<template>
  <section
    class="invoice-preview-wrapper"
    style="position: relative"
    aria-label="Détails du bon de remboursement logistique"
    role="region"
  >
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="returnVoucher === undefined"
    >
      <h4 class="alert-heading">
        Erreur lors de la récupération du bon de remboursement
      </h4>
      <div class="alert-body">
        Aucun bon de remboursement trouvé avec cet identifiant. Consultez la
        <b-link
          class="alert-link"
          :to="{ name: 'bon-a-rembourser-list' }"
        >
          liste des bons de remboursement
        </b-link>
        pour en sélectionner un autre.
      </div>
    </b-alert>
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement des détails du bon de remboursement"
      />
    </div>
    <b-row
      v-if="returnVoucher"
      class="invoice-preview"
    >
      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          v-if="returnVoucher"
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <div
            class="d-flex justify-content-center flex-md-row flex-column invoice-spacing mt-0"
          >
            <div style="color: black">
              <div class="logo-wrapper">
                <img
                  src="@/assets/images/logo/logo.png"
                  width="300px"
                  height="100%"
                >
                <h3 class="text-primary invoice-logo" />
              </div>
            </div>
          </div>

          <b-card-body class="d-flex flex-row justify-content-between">
            <div style="color: black">
              <div class="ml-1">
                <h1 style="color: black; font-size: 30px">
                  Bon de remboursement N° {{ returnVoucher.id }}
                </h1>
              </div>
              <div>
                <h3
                  class="ml-1"
                  style="color: black"
                >
                  Date de création :
                  <span style="color: black"> {{ formatFn(returnVoucher.created_at) }}</span>
                </h3>
              </div>
            </div>
            <div
              class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0 mr-1"
            >
              <!-- Header: Left Content -->

              <!-- Header: Right Content -->
              <div
                class="mb-2 mt-0 ml-2"
                style="border: 2px solid black"
              >
                <b-card-text
                  class="ml-2 mr-2 mt-2 mb-1 h3"
                  style="color: black"
                >
                  <strong>{{ company.name }} :</strong> Société logistique
                </b-card-text>
                <b-card-text
                  class="ml-2 h3 mr-2 mb-1"
                  style="color: black"
                >
                  <strong>Matricule fiscale :</strong> {{ company.tax_identification_number }}
                </b-card-text>
                <b-card-text
                  class="ml-2 mr-2 mb-1 h3"
                  style="color: black"
                >
                  <strong>Adresse :</strong> {{ company.address }}
                </b-card-text>
                <b-card-text
                  class="ml-2 mr-2 mb-1 h3"
                  style="color:black"
                >
                  <strong>Téléphone :</strong> {{ company.phone1 }}
                </b-card-text>
              </div>
            </div>

          </b-card-body>

          <!-- Summary Table -->
          <b-card-body class="invoice-padding pt-0">
            <b-table-lite
              class="ta summary-table mb-4"
              responsive
              :items="summaryData"
              :fields="summaryFields"
              aria-label="Tableau récapitulatif du bon de remboursement"
              role="table"
            />
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-card-body class="invoice-padding pt-0">
            <b-table-lite
              class="ta"
              responsive
              :items="orders"
              :fields="[
                'code',
                'fournisseur',
                'gouvernorat',
                'tel',
                'destinataire',
                'adresse',
                'prix'
              ]"
              aria-label="Tableau des colis associés au bon de remboursement"
              role="table"
            >
              <template
                #cell(code)="data"
              >
                <h5>{{ data.item.code }}</h5>
                <span
                  v-if="data.item.is_an_exchange"
                  style="color:red ; font-weight: bold;"
                  aria-label="Colis d'échange"
                > Échange </span>
              </template>
              <template #cell(taskDescription)="data">
                <b-card-text class="font-weight-bold mb-25">
                  {{ data.item.taskTitle }}{{ data.item.taskTitle }}
                </b-card-text>
                <b-card-text class="text-nowrap">
                  {{ data.item.taskDescription }}
                </b-card-text>
              </template>
            </b-table-lite>
          </b-card-body>
          <!-- Invoice Description: Total -->

          <b-card-body class="d-flex justify-content-between mt-5">
            <!-- Col: Total -->

            <div class="mb-5 ml-1">
              <h3 style="color: black; font-size: 25px">
                Signature
              </h3>
            </div>
            <div>
              <h1 style="color: black; font-size: 25px">
                Net à payer : {{ returnVoucher.net_payment }} DT
              </h1>

            </div>
          </b-card-body>

        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: Print -->
          <b-button
            v-if="!isLoading"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            :class="{ 'disabled-cursor': isLoading }"
            :disabled="isLoading"
            @click="printInvoice"
          >
            Imprimer
            <span class="sr-only">Imprimer le bon de remboursement logistique</span>
          </b-button>

          <!-- Button: Edit -->
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
  BLink,
  VBToggle,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
  },
  data() {
    return {
      returnVoucher: [],
      orders: [],
      company: {},
      isLoading: false,
      summaryFields: [
        { key: 'orders_count', label: 'Nombre de colis' },
        { key: 'total_cod', label: 'Total COD' },
        { key: 'frais_livraison', label: 'Frais de livraison' },
        { key: 'frais_douane', label: 'Frais douane' },
        { key: 'frais', label: 'Frais' },
        { key: 'net_payer', label: 'Net a payer' },
      ],
    }
  },
  computed: {
    summaryData() {
      if (!this.returnVoucher || !this.orders.length) return []

      const ordersCount = this.orders.length
      const totalCOD = this.returnVoucher.total_price || 0
      const fraisLivraison = this.returnVoucher.total_delivered_price || 0
      const fraisDouane = '-10%' // Static value as requested
      const frais = '1 DT' // Static value as requested
      const netPayment = this.returnVoucher.net_payment || 0

      return [
        {
          orders_count: ordersCount,
          total_cod: `${totalCOD.toFixed(3)}`,
          frais_livraison: `${fraisLivraison.toFixed(3)}`,
          frais_douane: fraisDouane,
          frais,
          net_payer: `${netPayment.toFixed(3)}`,
        },
      ]
    },
  },
  created() {
    this.company = storeAuth.state.company
    this.id = this.$route.params.id
    this.getReturnVoucher()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    async getReturnVoucher() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/return-vouchers/reimbursed/detail/${this.$route.params.id}/`)
        this.returnVoucher = data
        const { orders } = this.returnVoucher
        for (let i = 0; i < orders.length; i += 1) {
          const order = orders[i]
          const dataObj = {
            code: order.id,
            gouvernorat: order.governorate,
            fournisseur: order.supplier.username,
            tel: order.phone1,
            destinataire: order.name,
            adresse: order.address,
            prix: order.price_ttc,
            is_an_exchange: order.is_an_exchange,
          }
          this.orders.push(dataObj)
        }
      } catch (e) {
        // handle error if needed
      }
      this.isLoading = false
    },
  },
  setup() {
    const invoiceData = ref(null)
    const paymentDetails = ref({})
    const sommeFrais = 0
    const total = 0
    const tax = 0
    const sommeHT = 0
    const printInvoice = () => {
      window.print()
    }

    return {
      invoiceData,
      paymentDetails,
      printInvoice,
      sommeHT,
      tax,
      total,
      sommeFrais,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid black;
}
footer {
  bottom: 0;
  height: auto; /* Footer height */
}
table {
  width: 100%;
  border-collapse: collapse;
}
.mycolor {
  background-color: #EA2F2F;
}
.contact {
  background-color: #EA2F2F;
  padding: 10px;
  border-radius: 20px;
}

.summary-table {
  margin-bottom: 2rem;
}

.summary-table table {
  background-color: #f8f9fa;
}

.summary-table th {
  background-color: #e9ecef;
  font-weight: bold;
}

.summary-table td:last-child {
  text-align: right;
  font-weight: bold;
}
</style>

<style lang="scss">
@media print {
  .ta {
    overflow-x: visible;
    font-size: 20px;
    color: black;
  }
  footer {
    position: fixed;
    bottom: 0;
    width: 100%;
    height: auto; /* Footer height */
  }

  .table thead th {
    border: 2px solid black;
  }

  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  .mycolor {
    background-color: #EA2F2F;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .contact {
    background-color: #EA2F2F;
    padding: 20px;
    border-radius: 25px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  footer footer-light footer-static {
    display: inline-block;
  }
  footer.footer {
    display: none;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
  table,
  td,
  th {
    padding: 10px;
    text-align: left;
    border: 1px solid black;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }
}
</style>
